// hooks/useMediaData.js - CLEANED UP - Release-based structure ONLY
import { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { updateChallengeProgress } from '../utils/challengeUtils';

export const useMediaData = (supabase, currentUser, isAdmin, authLoading, options = {}) => {
  const { skipAutoCollection = false } = options;
  const { showSuccess, showError } = useNotification();

  // State
  const [masterItems, setMasterItems] = useState([]);
  const [allReleases, setAllReleases] = useState([]);
  const [userCollectionItems, setUserCollectionItems] = useState([]);
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [userWantlist, setUserWantlist] = useState([]);

  // Form states
  const [newMasterItem, setNewMasterItem] = useState({
    title: '',
    year: '',
    genre: '',
    director: '',
    runtime: '',
    description: '',
    actors: '',
    imdb_rating: '',
    country: '',
    language: '',
    rated: '',
    released: '',
    writer: '',
  });

  const [newCollectionItem, setNewCollectionItem] = useState({
    personal_notes: '',
    format: 'VHS',
  });

  // Load data functions
  const loadMasterItems = useCallback(async () => {
    const { data, error } = await supabase
      .from('media_titles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error loading master items:', error);
    } else {
      setMasterItems(data || []);
    }
  }, [supabase]);

  // Load all releases for stats
  const loadAllReleases = useCallback(async () => {
    const { data, error } = await supabase.from('movie_releases').select('*');

    if (error) {
      console.error('❌ Error loading all releases:', error);
    } else {
      setAllReleases(data || []);
    }
  }, [supabase]);

  // CLEANED: Load user collection - RELEASE-BASED ONLY
  const loadUserCollection = useCallback(async () => {
    if (!currentUser) {
      console.log('❌ Collection loading skipped - no user');
      setUserCollectionItems([]);
      return;
    }

    if (!currentUser.id) {
      console.log('❌ Collection loading skipped - no user ID');
      setUserCollectionItems([]);
      return;
    }

    try {
      // Load collection items - paginate to bypass PostgREST's 1000-row default cap
      let collectionItems = [];
      const PAGE_SIZE = 1000;
      let page = 0;
      while (true) {
        const { data: pageData, error: collectionError } = await supabase
          .from('user_collection_items')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

        if (collectionError) throw collectionError;
        if (!pageData || pageData.length === 0) break;
        collectionItems = [...collectionItems, ...pageData];
        if (pageData.length < PAGE_SIZE) break;
        page++;
      }

      if (collectionItems.length === 0) {
        // Collection is empty
        console.log('📭 Collection is empty, setting empty array');
        setUserCollectionItems([]);
        return;
      }

      // Process collection items

      // Separate release-based and master-only items
      const releaseBasedItems = collectionItems.filter(item => item.release_id !== null);
      const masterOnlyItems = collectionItems.filter(item => item.release_id === null);

      let enrichedItems = [];

      // Fire all enrichment batches in parallel
      const BATCH_SIZE = 50;

      const [releaseData, masterData] = await Promise.all([
        // Release-based: all batches in parallel
        (async () => {
          if (releaseBasedItems.length === 0) return [];
          const releaseIds = [
            ...new Set(releaseBasedItems.map(item => item.release_id).filter(id => id != null)),
          ];
          const batches = [];
          for (let i = 0; i < releaseIds.length; i += BATCH_SIZE) {
            batches.push(
              supabase
                .from('movie_releases')
                .select(
                  `*, media_titles!master_movie_id (
                    id, display_id, title, year, director, actors, genre, studio, runtime, description, cover_image_url, language, type, created_at, updated_at, added_by, imdb_rating
                  )`
                )
                .in('id', releaseIds.slice(i, i + BATCH_SIZE))
            );
          }
          const results = await Promise.all(batches);
          results.forEach(({ error }) => {
            if (error) throw error;
          });
          return results.flatMap(({ data }) => data || []);
        })(),

        // Master-only: all batches in parallel
        (async () => {
          if (masterOnlyItems.length === 0) return [];
          const masterIds = [
            ...new Set(masterOnlyItems.map(item => item.master_item_id).filter(id => id != null)),
          ];
          const batches = [];
          for (let i = 0; i < masterIds.length; i += BATCH_SIZE) {
            batches.push(
              supabase
                .from('media_titles')
                .select(
                  'id, display_id, title, year, director, actors, genre, studio, runtime, description, cover_image_url, language, type, created_at, updated_at, added_by, imdb_rating'
                )
                .in('id', masterIds.slice(i, i + BATCH_SIZE))
            );
          }
          const results = await Promise.all(batches);
          results.forEach(({ error }) => {
            if (error) throw error;
          });
          return results.flatMap(({ data }) => data || []);
        })(),
      ]);

      // Combine release-based items
      if (releaseBasedItems.length > 0) {
        const releaseMap = new Map(releaseData.map(r => [r.id, r]));
        enrichedItems = [
          ...enrichedItems,
          ...releaseBasedItems.map(item => ({
            ...item,
            movie_releases: releaseMap.get(item.release_id),
          })),
        ];
      }

      // Combine master-only items
      if (masterOnlyItems.length > 0) {
        const masterMap = new Map(masterData.map(m => [m.id, m]));
        enrichedItems = [
          ...enrichedItems,
          ...masterOnlyItems.map(item => ({
            ...item,
            movie_releases: {
              id: null,
              title: null,
              release_title: 'No associated release',
              format: item.format || 'No associated release',
              media_titles: masterMap.get(item.master_item_id),
            },
          })),
        ];
      }

      // Sort by created_at descending
      enrichedItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Collection loading complete
      console.log('✅ Collection loading complete! Setting', enrichedItems.length, 'items');
      setUserCollectionItems(enrichedItems);
    } catch (err) {
      console.error('❌ Error loading collection:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint,
      });

      // Always set empty array on error to prevent infinite loading
      setUserCollectionItems([]);

      // Show error to user if it's a permission or database issue
      if (err.code === 'PGRST301' || err.message?.includes('permission')) {
        showError('Unable to load collection. Please try refreshing the page.');
      }
    }
  }, [supabase, currentUser, showError]);

  // CLEANED: Load marketplace - RELEASE-BASED ONLY
  const loadMarketplaceListings = useCallback(async () => {
    try {
      // First get all active listings
      const { data: allListings, error } = await supabase
        .from('marketplace_listings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error loading marketplace listings:', error);
        setMarketplaceListings([]);
        return;
      }

      // Separate release-based and master-only listings
      const releaseBasedListings = allListings.filter(listing => listing.release_id !== null);
      const masterOnlyListings = allListings.filter(listing => listing.release_id === null);

      let enrichedListings = [];

      // Handle release-based listings
      if (releaseBasedListings.length > 0) {
        const { data: releaseEnrichedListings, error: releaseError } = await supabase
          .from('marketplace_listings')
          .select(
            `
            *,
            user_profiles (username, display_name),
            movie_releases (
              id,
              display_id,
              release_title,
              studio,
              distributor_code,
              case_type,
              format,
              front_cover_url,
              release_year,
              media_titles!master_movie_id (
                id, title, year, director, genre, studio, runtime, description, cover_image_url
              )
            )
          `
          )
          .in(
            'id',
            releaseBasedListings.map(l => l.id)
          );

        if (releaseError) {
          console.error('❌ Error loading release-based listings:', releaseError);
        } else {
          enrichedListings = [...enrichedListings, ...releaseEnrichedListings];
        }
      }

      // Handle master-only listings
      if (masterOnlyListings.length > 0) {
        // Get collection items to find master data
        const collectionItemIds = masterOnlyListings.map(l => l.collection_item_id);

        const { data: collectionItems, error: collectionError } = await supabase
          .from('user_collection_items')
          .select(
            `
            id,
            master_item_id,
            media_titles!master_item_id (
              id, title, year, director, genre, studio, runtime, description, cover_image_url
            )
          `
          )
          .in('id', collectionItemIds);

        if (collectionError) {
          console.error(
            '❌ Error loading collection items for master-only listings:',
            collectionError
          );
        } else {
          // Enrich master-only listings with movie data
          const masterEnrichedListings = masterOnlyListings.map(listing => {
            const collectionItem = collectionItems?.find(
              item => item.id === listing.collection_item_id
            );
            const masterData = collectionItem?.media_titles;

            return {
              ...listing,
              movie_releases: {
                id: null,
                display_id: null,
                release_title: 'No associated release',
                studio: null,
                case_type: null,
                format: null,
                front_cover_url: null,
                release_year: null,
                media_titles: masterData,
              },
              media_titles: masterData,
            };
          });

          enrichedListings = [...enrichedListings, ...masterEnrichedListings];
        }
      }

      // Sort by creation date
      const sortedListings = enrichedListings.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      // Transform data for backward compatibility in components
      const transformedData = sortedListings.map(listing => ({
        ...listing,
        // Add media_titles at top level for components that expect it there
        media_titles: listing.movie_releases?.media_titles || listing.media_titles || null,
      }));

      setMarketplaceListings(transformedData);
    } catch (err) {
      console.error('❌ Error in loadMarketplaceListings:', err);
      setMarketplaceListings([]);
    }
  }, [supabase]);

  // Load releases for a specific movie
  const loadMovieReleases = useCallback(
    async movieId => {
      if (!movieId) return [];

      try {
        const { data, error } = await supabase
          .from('movie_releases')
          .select('*')
          .eq('master_movie_id', movieId)
          .order('release_year', { ascending: false });

        if (error) {
          console.error('❌ Error loading releases:', error);
          return [];
        }

        const releases = data || [];

        // Add have/want counts to each release
        const releasesWithCounts = await Promise.all(
          releases.map(async release => {
            // Count how many users have this release in their collection
            const { count: haveCount } = await supabase
              .from('user_collection_items')
              .select('*', { count: 'exact', head: true })
              .eq('release_id', release.id);

            // Count how many users want this release
            const { count: wantCount } = await supabase
              .from('user_wantlists')
              .select('*', { count: 'exact', head: true })
              .eq('release_id', release.id);

            return {
              ...release,
              haveCount: haveCount || 0,
              wantCount: wantCount || 0,
            };
          })
        );

        return releasesWithCounts;
      } catch (err) {
        console.error('❌ Error loading releases:', err);
        return [];
      }
    },
    [supabase]
  );

  // Load existing images for a master item (for editing)
  const loadMasterItemImages = useCallback(
    async masterItemId => {
      if (!masterItemId) return [];

      try {
        // Loading master item images

        const { data, error } = await supabase
          .from('master_item_images')
          .select('*')
          .eq('master_item_id', masterItemId)
          .order('image_order');

        if (error) {
          console.error('❌ Database error loading images:', error);
          throw error;
        }

        if (!data || data.length === 0) {
          // No images found
          return [];
        }

        // Format images for the form
        const formattedImages = data.map((img, index) => ({
          id: img.id,
          url: img.image_url,
          preview: img.image_url,
          caption: img.caption || '',
          isPrimary: img.is_primary || false,
          order: img.image_order || index + 1,
          isNew: false,
          file: null,
        }));

        // Images formatted for form
        return formattedImages;
      } catch (error) {
        console.error('❌ Error loading master item images:', error);
        return [];
      }
    },
    [supabase]
  );

  // Load existing images for a release (for editing)
  const loadReleaseImages = useCallback(
    async releaseId => {
      if (!releaseId) return [];

      try {
        console.log('🖼️ Loading release images for ID:', releaseId);

        const { data, error } = await supabase
          .from('release_images')
          .select('*')
          .eq('release_id', releaseId)
          .order('image_order');

        if (error) {
          console.error('❌ Database error loading release images:', error);
          throw error;
        }

        if (!data || data.length === 0) {
          console.log('📭 No release images found in database');
          return [];
        }

        // Format images for the form
        const formattedImages = data.map((img, index) => ({
          id: img.id,
          url: img.image_url,
          preview: img.image_url,
          caption: img.caption || '',
          isPrimary: img.is_primary || false,
          order: img.image_order || index + 1,
          isNew: false,
          file: null,
        }));

        console.log('✅ Formatted release images for form:', formattedImages);
        return formattedImages;
      } catch (error) {
        console.error('❌ Error loading release images:', error);
        return [];
      }
    },
    [supabase]
  );

  // Upload multiple images to storage for master items
  const uploadMasterItemImages = async (images, movieTitle) => {
    if (!images || images.length === 0) return [];

    console.log('📤 Uploading master item images:', images);

    const uploadPromises = images.map(async (image, index) => {
      if (!image.isNew || !image.file) {
        console.log('⏭️ Skipping image (not new or no file):', image);
        return image;
      }

      console.log('📤 Uploading new image:', image);

      try {
        const fileName = `master-${movieTitle.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}-${index}`;
        const fileExt = image.file.name.split('.').pop();
        const filePath = `movie-posters/${fileName}.${fileExt}`;

        const { error } = await supabase.storage.from('media-images').upload(filePath, image.file, {
          cacheControl: '3600',
          upsert: true,
        });

        if (error) {
          console.error('❌ Storage upload error:', error);
          throw error;
        }

        const { data: publicData } = supabase.storage.from('media-images').getPublicUrl(filePath);

        console.log('✅ Image uploaded successfully:', publicData.publicUrl);

        return {
          ...image,
          url: publicData.publicUrl,
          preview: publicData.publicUrl,
        };
      } catch (error) {
        console.error('❌ Error uploading image:', error);
        throw error;
      }
    });

    const uploadedImages = await Promise.all(uploadPromises);
    console.log('✅ All images processed:', uploadedImages);
    return uploadedImages;
  };

  // Save master item images to database
  const saveMasterItemImages = async (masterItemId, images) => {
    try {
      console.log('💾 Saving master item images...');
      console.log('🎯 Master Item ID:', masterItemId);
      console.log('🖼️ Images:', images);

      // Get current images from database
      const { data: currentDbImages, error: fetchError } = await supabase
        .from('master_item_images')
        .select('*')
        .eq('master_item_id', masterItemId);

      if (fetchError) {
        console.error('❌ Error fetching current images:', fetchError);
        throw fetchError;
      }

      console.log('💾 Current images in database:', currentDbImages);

      // Separate images by type
      const existingImages = images.filter(
        img => !img.isNew && img.id && typeof img.id === 'number'
      );

      const fallbackImages = images.filter(
        img => !img.isNew && img.id && typeof img.id === 'string'
      );

      // Log any problematic images for debugging
      const problematicImages = images.filter(
        img =>
          !img.isNew && img.id && typeof img.id === 'string' && img.id.includes('existing-cover')
      );
      if (problematicImages.length > 0) {
        console.warn(
          '⚠️ Found existing-cover images that will be converted to new images:',
          problematicImages
        );
      }

      const newImagesWithUrls = images.filter(img => img.isNew && img.url);

      console.log(
        '📋 Existing database images to keep:',
        existingImages.map(img => ({ id: img.id, type: typeof img.id }))
      );
      console.log(
        '🔄 Fallback images to convert:',
        fallbackImages.map(img => ({ id: img.id, type: typeof img.id }))
      );
      console.log('✨ New uploaded images to insert:', newImagesWithUrls.length);

      // Find images to delete
      const formImageIds = existingImages.map(img => img.id);
      console.log(
        '🆔 Form image IDs to keep:',
        formImageIds.map(id => ({ id, type: typeof id }))
      );

      const imagesToDelete =
        currentDbImages?.filter(dbImg => !formImageIds.includes(dbImg.id)) || [];

      console.log('🗑️ Images to delete:', imagesToDelete);

      // 1. DELETE removed images
      if (imagesToDelete.length > 0) {
        const deleteIds = imagesToDelete.map(img => img.id).filter(id => typeof id === 'number');
        console.log('🗑️ Final delete IDs (numbers only):', deleteIds);

        if (deleteIds.length > 0) {
          const { error: deleteError } = await supabase
            .from('master_item_images')
            .delete()
            .in('id', deleteIds);

          if (deleteError) {
            console.error('❌ Error deleting images:', deleteError);
            throw deleteError;
          }

          console.log('✅ Deleted images:', deleteIds);
        } else {
          console.log('⚠️ No valid numeric IDs to delete');
        }

        // Clean up storage files
        try {
          const filePaths = imagesToDelete.map(img => {
            const url = new URL(img.image_url);
            return url.pathname.split('/').slice(-1)[0];
          });

          await supabase.storage.from('media-images').remove(filePaths);

          console.log('🧹 Cleaned up storage files');
        } catch (storageError) {
          console.error('⚠️ Storage cleanup error (non-fatal):', storageError);
        }
      }

      // 2. UPDATE existing images
      for (let i = 0; i < existingImages.length; i++) {
        const image = existingImages[i];
        if (image.id && typeof image.id === 'number') {
          const updateData = {
            image_order: image.order || i + 1,
            caption: image.caption || null,
            is_primary: image.isPrimary || false,
          };

          const { error: updateError } = await supabase
            .from('master_item_images')
            .update(updateData)
            .eq('id', image.id);

          if (updateError) {
            console.error('❌ Error updating image:', image.id, updateError);
            throw updateError;
          }

          console.log(`✅ Updated image ${image.id}`);
        } else {
          console.warn(`⚠️ Skipping image update - invalid ID:`, {
            id: image.id,
            type: typeof image.id,
          });
        }
      }

      // 3. INSERT new images (including converted fallback images)
      const allNewImages = [...fallbackImages, ...newImagesWithUrls];

      if (allNewImages.length > 0) {
        const newImageData = allNewImages.map((image, index) => {
          if (!image.url) {
            throw new Error(`New image at index ${index} missing URL`);
          }

          // Only include database fields - explicitly exclude any 'id' field
          return {
            master_item_id: masterItemId,
            image_url: image.url,
            image_order: image.order || existingImages.length + index + 1,
            caption: image.caption || null,
            is_primary: image.isPrimary || false,
            uploaded_by: currentUser.id,
          };
        });

        console.log('📤 Inserting new images:', newImageData);

        // Double-check that no invalid fields are being inserted
        const safeImageData = newImageData.map(data => {
          const { id, ...safeData } = data; // Remove any 'id' field that might have slipped through
          return safeData;
        });

        console.log('📤 Safe image data for insertion:', safeImageData);

        const { error: insertError } = await supabase
          .from('master_item_images')
          .insert(safeImageData);

        if (insertError) {
          console.error('❌ Error inserting new images:', insertError);
          throw insertError;
        }

        console.log('✅ Inserted new images');
      }

      // 4. UPDATE master item cover_image_url with primary image
      const allImages = [...existingImages, ...allNewImages];
      const primaryImage = allImages.find(img => img.isPrimary) || allImages[0];

      if (primaryImage?.url) {
        const { error: coverUpdateError } = await supabase
          .from('media_titles')
          .update({ cover_image_url: primaryImage.url })
          .eq('id', masterItemId);

        if (coverUpdateError) {
          console.error('⚠️ Error updating cover image:', coverUpdateError);
        } else {
          console.log('✅ Updated cover image URL:', primaryImage.url);
        }
      }

      console.log('🎉 Master item images saved successfully!');
    } catch (error) {
      console.error('❌ Error saving master item images:', error);
      throw error;
    }
  };

  // Clean up master item images when deleting a master item
  const cleanupMasterItemImages = async masterItemId => {
    try {
      const { data: masterImages } = await supabase
        .from('master_item_images')
        .select('image_url')
        .eq('master_item_id', masterItemId);

      if (masterImages?.length > 0) {
        const filePaths = masterImages.map(img => {
          const url = new URL(img.image_url);
          return url.pathname.split('/').slice(-1)[0];
        });

        const { error: storageError } = await supabase.storage
          .from('media-images')
          .remove(filePaths);

        if (storageError) {
          console.error('⚠️ Error deleting master item images from storage:', storageError);
        }
      }

      const { error: dbError } = await supabase
        .from('master_item_images')
        .delete()
        .eq('master_item_id', masterItemId);

      if (dbError) {
        console.error('⚠️ Error deleting master item images from database:', dbError);
      }
    } catch (error) {
      console.error('❌ Error cleaning up master item images:', error);
    }
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadMasterItems();
    loadAllReleases();
    loadMarketplaceListings();
  }, [loadMasterItems, loadAllReleases, loadMarketplaceListings]); // Include function dependencies

  useEffect(() => {
    // Don't load collection until auth is ready
    if (authLoading) {
      return;
    }

    // Skip automatic collection loading if requested
    if (skipAutoCollection) {
      return;
    }

    if (currentUser) {
      loadUserCollection();
    } else {
      setUserCollectionItems([]);
    }
  }, [currentUser, loadUserCollection, authLoading, skipAutoCollection]); // Include authLoading dependency

  // Validation functions
  const validateMasterItem = item => {
    const errors = [];

    if (!item.title || !item.title.trim()) {
      errors.push('Title is required');
    }

    if (!item.year || !item.year.trim()) {
      errors.push('Year is required');
    } else {
      const yearNum = parseInt(item.year);
      if (!/^\d{4}$/.test(item.year) || yearNum < 1800 || yearNum > 2200) {
        errors.push('Year must be a 4-digit number between 1800 and 2200');
      }
    }

    return errors;
  };

  const checkForDuplicates = async (
    title,
    year,
    tmdbId = null,
    editingItemId = null,
    itemType = null
  ) => {
    // Normalize title for better matching
    const normalizedTitle = title
      .trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .toLowerCase();

    try {
      // Start with base query
      let query = supabase.from('media_titles').select('id, title, year, tmdb_id');

      // Build safe OR conditions using proper parameter binding
      const conditions = [];

      // Always check title + year using safe parameter binding
      conditions.push(`and(title.ilike."${normalizedTitle}",year.eq.${parseInt(year)})`);

      // If TMDB ID provided, also check for TMDB duplicates scoped to the same type
      // (TMDB IDs are per-type: same number can exist as both a movie and a TV show)
      if (tmdbId && tmdbId !== 0 && tmdbId !== '0') {
        const tmdbCondition = itemType
          ? `and(tmdb_id.eq.${parseInt(tmdbId)},type.eq.${itemType})`
          : `tmdb_id.eq.${parseInt(tmdbId)}`;
        conditions.push(tmdbCondition);
      }

      // Apply OR conditions
      query = query.or(conditions.join(','));

      // Exclude the item being edited
      if (editingItemId) {
        query = query.neq('id', editingItemId);
      }

      const { data, error } = await query.limit(1);

      if (error) {
        console.error('Error checking for duplicates:', error);
        return false; // On error, allow the operation to continue
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error in checkForDuplicates:', error);
      return false; // On error, allow the operation to continue
    }
  };

  // CLEANED: handleAddMasterItem - same as before but cleaner
  const handleAddMasterItem = async (
    uploadImageToStorage, // not used for multi-image
    selectedImages,
    imageError,
    setUploadingImage,
    editingMasterItem,
    addToMyCollection,
    userProfile,
    setActivityFeed,
    activityFeed,
    masterItemData = null // Optional parameter to override newMasterItem
  ) => {
    console.log('🚀 handleAddMasterItem called with:');
    console.log('   - editingMasterItem:', !!editingMasterItem);
    console.log('   - selectedImages:', selectedImages?.length || 0, 'images');
    console.log(
      '   - selectedImages sample:',
      selectedImages?.slice(0, 2)?.map(img => ({
        id: img.id,
        idType: typeof img.id,
        isNew: img.isNew,
        url: img.url?.substring(0, 50) + '...',
      }))
    );
    try {
      setUploadingImage(true);
      console.log('🚀 Starting handleAddMasterItem');
      console.log('✏️ Editing mode:', !!editingMasterItem);
      console.log('🖼️ Selected images:', selectedImages);

      // Use provided data or fall back to context
      const itemToSave = masterItemData || newMasterItem;

      // Validate required fields
      const validationErrors = validateMasterItem(itemToSave);
      if (validationErrors.length > 0) {
        showError('Validation errors: ' + validationErrors.join(', '));
        return { success: false, errors: validationErrors };
      }

      // Kick off TMDB poster fetch early so it runs in parallel with the duplicate check
      const pendingUrl = !editingMasterItem && window.pendingPosterDownload?.url;
      const tmdbPosterFetchPromise =
        pendingUrl && pendingUrl.includes('image.tmdb.org')
          ? fetch(pendingUrl)
              .then(r => (r.ok ? r.blob() : null))
              .catch(() => null)
          : null;

      // Check for duplicates (skip for editing existing item)
      if (!editingMasterItem) {
        const isDuplicate = await checkForDuplicates(
          itemToSave.title,
          itemToSave.year,
          itemToSave.tmdb_id,
          editingMasterItem?.id,
          itemToSave.type || null
        );

        if (isDuplicate) {
          const message = itemToSave.tmdb_id
            ? 'A movie with this TMDB ID or title/year already exists in the catalog!'
            : 'A movie with this title and year already exists in the catalog!';
          showError(message);
          return { success: false, error: 'Duplicate movie' };
        }
      }

      // Process images
      let finalCoverImageUrl = itemToSave.cover_image_url || null;
      let processedImages = [];

      if (selectedImages && selectedImages.length > 0) {
        console.log('🖼️ Processing images:', selectedImages.length);

        const existingImages = selectedImages.filter(
          img => img.isNew === false && (img.url || img.preview) && !img.file
        );
        console.log('📋 Existing images found:', existingImages.length, existingImages);

        const newImagesToUpload = selectedImages.filter(img => img.isNew === true && img.file);
        console.log('📤 New images to upload:', newImagesToUpload.length, newImagesToUpload);

        // Upload new images in parallel
        const uploadedImages = await Promise.all(
          newImagesToUpload.map(async (image, i) => {
            const fileName = `master-${itemToSave.title.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}-${i}`;
            const fileExt = image.file.name.split('.').pop();
            const filePath = `movie-posters/${fileName}.${fileExt}`;

            const { error } = await supabase.storage
              .from('media-images')
              .upload(filePath, image.file, { cacheControl: '3600', upsert: true });

            if (error) throw new Error(`Failed to upload image: ${error.message}`);

            const { data: publicData } = supabase.storage
              .from('media-images')
              .getPublicUrl(filePath);

            return {
              url: publicData.publicUrl,
              caption: image.caption || '',
              isPrimary: image.isPrimary || false,
              order: image.order || existingImages.length + i + 1,
              isNew: true,
              file: null,
            };
          })
        );

        // Combine images while preserving existing image properties
        processedImages = [
          ...existingImages.map(img => ({
            id: img.id,
            url: img.url || img.preview,
            caption: img.caption || '',
            isPrimary: img.isPrimary || false,
            order: img.order || 1,
            isNew: false,
          })),
          ...uploadedImages,
        ];

        console.log('🎯 Final processed images:', processedImages);

        // Set primary cover image
        // eslint-disable-next-line no-unused-vars
        let shouldUpdateCoverUrl = false;

        if (processedImages.length > 0) {
          const primaryImage = processedImages.find(img => img.isPrimary) || processedImages[0];
          if (primaryImage?.url) {
            // Only update cover if we have user-uploaded images
            finalCoverImageUrl = primaryImage.url;
            console.log('🖼️ Primary cover set to user image:', finalCoverImageUrl);
          }
        } else if (itemToSave.cover_image_url && !finalCoverImageUrl) {
          // Keep the existing API poster if no user images uploaded
          finalCoverImageUrl = itemToSave.cover_image_url;
          console.log('🖼️ Keeping API poster:', finalCoverImageUrl);
        }
      }

      // Handle pending poster download (from movie search)
      // Handle pending poster download (from movie search) - only for new movies
      if (!finalCoverImageUrl && window.pendingPosterDownload && !editingMasterItem) {
        // Check if URL is already a Supabase storage URL (processed server-side)
        if (window.pendingPosterDownload.url.includes('supabase.co/storage')) {
          console.log(
            '✅ Using server-processed Supabase storage URL:',
            window.pendingPosterDownload.url
          );
          finalCoverImageUrl = window.pendingPosterDownload.url;
        } else if (
          window.pendingPosterDownload.url &&
          window.pendingPosterDownload.url.startsWith('https://') &&
          !window.pendingPosterDownload.url.includes('image.tmdb.org')
        ) {
          finalCoverImageUrl = window.pendingPosterDownload.url;
        } else {
          try {
            // Use the pre-fetched blob if available (started in parallel with duplicate check)
            const blob = tmdbPosterFetchPromise
              ? await tmdbPosterFetchPromise
              : await fetch(window.pendingPosterDownload.url).then(r => {
                  if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
                  return r.blob();
                });

            if (!blob) throw new Error('Poster fetch returned no data');

            const fileName = `master-${itemToSave.title.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}-poster`;
            const filePath = `movie-posters/${fileName}.jpg`;

            const { error } = await supabase.storage.from('media-images').upload(filePath, blob, {
              cacheControl: '3600',
              upsert: true,
            });

            if (!error) {
              const { data: publicData } = supabase.storage
                .from('media-images')
                .getPublicUrl(filePath);

              finalCoverImageUrl = publicData.publicUrl;
            }
          } catch (downloadError) {
            console.error('❌ Failed to download poster:', downloadError);
            finalCoverImageUrl = window.pendingPosterDownload.url;
          }
        }

        window.pendingPosterDownload = null;
      }

      // Prepare master item data
      const masterItemDataForDB = {
        title: itemToSave.title,
        year: itemToSave.year, // FIXED: Missing year field!
        director: itemToSave.director || null,
        actors: itemToSave.actors || null,
        genre: itemToSave.genre || null,
        runtime: itemToSave.runtime || null,
        description: itemToSave.description || null,
        notes: itemToSave.notes || null,
        imdb_rating: itemToSave.imdb_rating || null,
        country: itemToSave.country || null,
        language: itemToSave.language || null,
        rated: itemToSave.rated || null,
        released: itemToSave.released || null,
        writer: itemToSave.writer || null,
        producer: itemToSave.producer || null,
        distributor: itemToSave.distributor || null,
        boxoffice: itemToSave.boxoffice || null,
        // Additional TMDB fields (saved but not displayed in UI)
        imdb_id: itemToSave.imdb_id || null,
        type: itemToSave.type || null,
        // NEW TMDB fields
        tmdb_id: itemToSave.tmdb_id || null,
        production_companies: itemToSave.production_companies || null,
        budget: itemToSave.budget || null,
        trailer_url: itemToSave.trailer_url || null,
        tagline: itemToSave.tagline || null,
        alternate_title: itemToSave.alternate_title || null,
        backdrop_url: itemToSave.backdrop_url || null,
        // TV-specific fields
        network: itemToSave.network || null,
        number_of_seasons: itemToSave.number_of_seasons || null,
        number_of_episodes: itemToSave.number_of_episodes || null,
        show_status: itemToSave.show_status || null,
        last_air_date: itemToSave.last_air_date || null,
        cover_image_url: finalCoverImageUrl,
        updated_at: new Date().toISOString(),
      };

      if (editingMasterItem) {
        // Editing existing master item
        console.log('✏️ Updating existing master item:', editingMasterItem.id);

        const { data: updatedMaster, error: masterError } = await supabase
          .from('media_titles')
          .update(masterItemDataForDB)
          .eq('id', editingMasterItem.id)
          .select();

        if (masterError) {
          console.error('❌ Error updating master item:', masterError);
          throw masterError;
        }

        console.log('✅ Master item update result:', {
          updatedRows: updatedMaster?.length,
          firstRow: updatedMaster?.[0]?.id,
        });

        if (processedImages.length > 0) {
          await saveMasterItemImages(editingMasterItem.id, processedImages);
        }

        console.log('✅ Movie updated successfully!');
        await loadMasterItems();

        // If we're on a movie detail page, reload to show updated poster
        if (
          window.location.pathname.includes('/catalog/') &&
          window.location.pathname !== '/catalog'
        ) {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }

        return { success: true, data: updatedMaster };
      } else {
        // Creating new master item
        console.log('🆕 Creating new master item');

        if (currentUser) {
          masterItemDataForDB.added_by = currentUser.id;
        }

        const { data: masterData, error: masterError } = await supabase
          .from('media_titles')
          .insert([masterItemDataForDB])
          .select()
          .single();

        if (masterError) {
          console.error('❌ Error creating master item:', masterError);

          // Handle database-level duplicate constraint violations
          if (masterError.code === '23505') {
            // PostgreSQL unique constraint violation
            if (masterError.message.includes('idx_media_titles_unique_tmdb_id')) {
              showError('This title already exists in the catalog!');
            } else if (masterError.message.includes('idx_media_titles_unique_title_year')) {
              showError('A movie with this title and year already exists in the catalog!');
            } else {
              showError('This movie already exists in the catalog!');
            }
            return { success: false, error: 'Duplicate movie detected by database' };
          }

          throw masterError;
        }

        console.log('✅ Master item created:', masterData);

        if (processedImages.length > 0) {
          await saveMasterItemImages(masterData.id, processedImages);
        }

        console.log('✅ Movie created successfully!');
        await loadMasterItems();
        return { success: true, data: masterData };
      }
    } catch (error) {
      console.error('❌ Error in handleAddMasterItem:', error);
      showError('Error saving movie: ' + error.message);
      return { success: false, error: error.message };
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEditMasterItem = item => {
    const formData = {
      title: item.title || '',
      year: item.year || '',
      director: item.director || '',
      producer: item.producer || '',
      writer: item.writer || '',
      distributor: item.distributor || '',
      actors: item.actors || '',
      genre: item.genre || '',
      studio: item.studio || '',
      runtime: item.runtime || '',
      description: item.description || '',
      notes: item.notes || '',
      imdb_rating: item.imdb_rating || '',
      cover_image_url: item.cover_image_url || '',
      released: item.released || '',
      rated: item.rated || '',
      boxoffice: item.boxoffice || '',
      country: item.country || '',
      language: item.language || '',
      // NEW TMDB fields for editing
      tmdb_id: item.tmdb_id || null,
      production_companies: item.production_companies || null,
      budget: item.budget || null,
      trailer_url: item.trailer_url || '',
      tagline: item.tagline || '',
      alternate_title: item.alternate_title || '',
    };

    setNewMasterItem(formData);

    return item;
  };

  const handleDeleteMasterItem = async id => {
    if (!isAdmin) {
      showError('Only admins can delete master items');
      return;
    }

    // Server-side admin verification - double-check admin status
    const { data: adminData, error: adminError } = await supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', currentUser.id)
      .limit(1);

    if (adminError || !adminData || adminData.length === 0) {
      console.error('Admin verification failed:', adminError);
      showError('Access denied. Admin privileges required.');
      return;
    }

    try {
      await cleanupMasterItemImages(id);

      console.log('Attempting to delete master item with ID:', id);
      const { error, data } = await supabase.from('media_titles').delete().eq('id', id);

      console.log('Delete result:', { error, data });

      if (error) {
        console.error('Delete error details:', error);
        showError('Error deleting movie: ' + error.message);
      } else {
        console.log('Delete successful');
        showSuccess('Movie deleted successfully!');
        loadMasterItems();
        // Navigate away from the deleted item's page
        window.location.href = '/catalog';
      }
    } catch (error) {
      console.error('❌ Error deleting master item:', error);
      showError('Error deleting master item: ' + error.message);
    }
  };

  // CLEANED: Collection operations - RELEASE-BASED ONLY
  const addToCollection = async masterItem => {
    if (!currentUser || !masterItem) {
      showError('Error: Missing user or master item');
      return;
    }

    // Always add master-only item (no release checking needed - UI handles specificity)
    try {
      const { error } = await supabase.from('user_collection_items').insert({
        user_id: currentUser.id,
        master_item_id: masterItem.id,
        release_id: null,
        personal_notes: newCollectionItem.personal_notes || null,
        format: newCollectionItem.format || null,
      });

      if (error) {
        if (error.code === '23505') {
          showError('You already have this movie in your collection!');
        } else {
          showError('Error adding to collection: ' + error.message);
        }
        return;
      }

      // Update challenge progress
      await updateChallengeProgress(supabase, currentUser.id, masterItem);

      showSuccess(`Added "${masterItem.title}" to your collection!`);
      await loadUserCollection();
    } catch (error) {
      showError('Error adding to collection: ' + error.message);
    }
  };

  // Add specific release to collection
  const addToCollectionWithRelease = async (releaseId, personalNotes = '') => {
    if (!currentUser || !releaseId) {
      showError('Error: Missing user or release');
      return;
    }

    try {
      // Get release details first to get the master movie ID
      const release = allReleases.find(r => r.id === releaseId);
      if (!release) {
        showError('Release not found');
        return;
      }

      const { error } = await supabase.from('user_collection_items').insert({
        user_id: currentUser.id,
        release_id: releaseId,
        master_item_id: release.master_movie_id, // Fix: Include master_item_id
        personal_notes: personalNotes || null,
      });

      if (error) {
        if (error.code === '23505') {
          showError('You already have this release in your collection!');
        } else {
          showError('Error adding to collection: ' + error.message);
        }
      } else {
        // Release details already fetched above
        if (release) {
          await updateChallengeProgress(supabase, currentUser.id, release);
        }

        showSuccess('Added to your collection!');
        loadUserCollection();
        // Reset form
        setNewCollectionItem({
          personal_notes: '',
        });
      }
    } catch (err) {
      console.error('❌ Error adding to collection:', err);
      showError('Error adding to collection: ' + err.message);
    }
  };

  const removeFromCollection = useCallback(
    async collectionItemId => {
      if (!currentUser) return;

      const { error } = await supabase
        .from('user_collection_items')
        .delete()
        .eq('id', collectionItemId);

      if (error) {
        showError('Error removing from collection: ' + error.message);
      } else {
        showSuccess('Removed from your collection!');
        loadUserCollection();
      }
    },
    [currentUser, supabase, showError, showSuccess, loadUserCollection]
  );

  // Update collection item notes
  const updateCollectionNotes = async (collectionItemId, notes) => {
    if (!currentUser) return;

    try {
      const { error } = await supabase
        .from('user_collection_items')
        .update({ personal_notes: notes })
        .eq('id', collectionItemId)
        .eq('user_id', currentUser.id);

      if (error) {
        showError('Error updating notes: ' + error.message);
      } else {
        loadUserCollection();
      }
    } catch (err) {
      console.error('❌ Error updating notes:', err);
      showError('Error updating notes: ' + err.message);
    }
  };

  const addMarketplaceListing = async (collectionItemId, price, condition, sellerNotes) => {
    if (!currentUser) {
      showError('Please log in to list items for sale');
      return;
    }

    console.log('💰 Adding marketplace listing for collection item:', collectionItemId);

    try {
      // Find the collection item
      const collectionItem = userCollectionItems.find(item => item.id === collectionItemId);
      if (!collectionItem) {
        showError('Collection item not found');
        return;
      }

      console.log('📦 Collection item found:', collectionItem);

      // Handle both release-based and master-only items
      const releaseId = collectionItem.release_id;
      const masterId = collectionItem.master_item_id;

      if (!releaseId && !masterId) {
        showError(
          'This collection item is missing both release and master data. Please contact support.'
        );
        return;
      }

      // Check if user already has an active listing for this collection item
      const { data: existingListing, error: checkError } = await supabase
        .from('marketplace_listings')
        .select('id, is_active, price, condition')
        .eq('seller_id', currentUser.id)
        .eq('collection_item_id', collectionItemId)
        .eq('is_active', true)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingListing) {
        showError('You already have this item listed for sale!');
        return;
      }

      // Create the listing data
      const listingData = {
        seller_id: currentUser.id,
        collection_item_id: collectionItemId,
        release_id: releaseId,
        price: parseFloat(price),
        condition: condition,
        seller_notes: sellerNotes || null,
        is_active: true,
        created_at: new Date().toISOString(),
      };

      console.log('📤 Creating listing with collection_item_id:', listingData);

      // Use different select query based on whether item has release data
      let selectQuery;
      if (releaseId) {
        // For items with release data
        selectQuery = `
          *,
          user_profiles (username, display_name),
          movie_releases (
            id,
            display_id,
            release_title,
            studio,
            case_type,
            format,
            front_cover_url,
            release_year,
            media_titles!master_movie_id (
              id, title, year, director, genre, studio, runtime, description, cover_image_url
            )
          )
        `;
      } else {
        // For items without release data (master-only items)
        selectQuery = `
          *,
          user_profiles (username, display_name)
        `;
      }

      const { data: newListing, error: insertError } = await supabase
        .from('marketplace_listings')
        .insert([listingData])
        .select(selectQuery)
        .single();

      if (insertError) {
        console.error('❌ Error creating listing:', insertError);
        if (insertError.code === '23505') {
          showError('You already have this item listed! Please refresh the page.');
        } else {
          showError('Error creating listing: ' + insertError.message);
        }
        return;
      }

      // For master-only items, we need to get the master data separately
      let processedListing = { ...newListing };

      if (!releaseId && masterId) {
        // Get master data from the collection item
        const collectionItemData = userCollectionItems.find(item => item.id === collectionItemId);
        const masterMediaTitles = collectionItemData?.movie_releases?.media_titles;

        // Create a fake movie_releases structure for consistency
        processedListing = {
          ...newListing,
          movie_releases: {
            id: null,
            display_id: null,
            release_title: 'No associated release',
            studio: null,
            case_type: null,
            format: null,
            front_cover_url: null,
            release_year: null,
            media_titles: masterMediaTitles,
          },
          media_titles: masterMediaTitles,
        };
      } else {
        processedListing = {
          ...newListing,
          media_titles: newListing.movie_releases?.media_titles || null,
        };
      }

      setMarketplaceListings(prev => [processedListing, ...prev]);

      showSuccess('Item listed for sale successfully!');
      console.log(
        '✅ Listing created with collection_item_id:',
        processedListing.collection_item_id
      );
    } catch (error) {
      console.error('❌ Error in addMarketplaceListing:', error);
      showError('Error creating listing: ' + error.message);
    }
  };
  // Remove marketplace listing
  const removeMarketplaceListing = useCallback(
    async (collectionItemId, isAdminDelete = false) => {
      if (!currentUser) return;

      console.log(
        '🗑️ Removing marketplace listing for collection item:',
        collectionItemId,
        'Admin delete:',
        isAdminDelete
      );

      try {
        // Build query - admins can delete any listing, regular users only their own
        let deleteQuery = supabase
          .from('marketplace_listings')
          .delete()
          .eq('collection_item_id', collectionItemId)
          .eq('is_active', true);

        // Only add seller_id restriction for non-admin deletions
        if (!isAdminDelete) {
          deleteQuery = deleteQuery.eq('seller_id', currentUser.id);
        }

        const { error: deleteError } = await deleteQuery;

        if (deleteError) {
          throw deleteError;
        }

        // Remove from local state - for admin deletes, remove any listing with this collection_item_id
        if (isAdminDelete) {
          setMarketplaceListings(prev =>
            prev.filter(item => item.collection_item_id !== collectionItemId)
          );
        } else {
          setMarketplaceListings(prev =>
            prev.filter(
              item =>
                !(item.collection_item_id === collectionItemId && item.seller_id === currentUser.id)
            )
          );
        }

        showSuccess(isAdminDelete ? 'Listing removed by admin!' : 'Listing removed successfully!');
        console.log('✅ Listing deleted from database');
      } catch (error) {
        console.error('❌ Error removing listing:', error);
        showError('Error removing listing: ' + error.message);
      }
    },
    [currentUser, supabase, showError, showSuccess]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Combined function to remove from marketplace AND collection
  const removeFromCollectionAndMarketplace = useCallback(
    async collectionItemId => {
      if (!currentUser) return;

      // Check if item is listed in marketplace
      const isListed = marketplaceListings?.some(
        listing =>
          listing.collection_item_id === collectionItemId && listing.seller_id === currentUser.id
      );

      if (isListed) {
        const confirmMessage =
          'This item is listed in the marketplace. Removing it will also remove the marketplace listing. Continue?';
        if (!window.confirm(confirmMessage)) {
          return; // User cancelled
        }

        // First remove from marketplace
        try {
          await removeMarketplaceListing(collectionItemId);
        } catch (error) {
          console.error('Error removing marketplace listing:', error);
          showError('Error removing marketplace listing. Cannot proceed with collection removal.');
          return;
        }
      } else {
        // Just confirm collection removal
      }

      // Then remove from collection
      await removeFromCollection(collectionItemId);
    },
    [currentUser, marketplaceListings, removeMarketplaceListing, showError, removeFromCollection]
  );

  // Check if a collection item is listed for sale
  const isItemListed = useCallback(
    collectionItem => {
      if (!currentUser || !collectionItem) return false;

      // Check if this collection item has an active listing by this user
      const hasListing = marketplaceListings.some(listing => {
        // Check by collection_item_id (most direct)
        if (
          listing.collection_item_id === collectionItem.id &&
          listing.seller_id === currentUser.id
        ) {
          return true;
        }

        // Also check by release_id if available
        if (
          collectionItem.release_id &&
          listing.release_id === collectionItem.release_id &&
          listing.seller_id === currentUser.id
        ) {
          return true;
        }

        return false;
      });

      return hasListing;
    },
    [marketplaceListings, currentUser]
  );

  // Reset form function
  const resetForms = () => {
    setNewMasterItem({
      title: '',
      year: '',
      genre: '',
      director: '',
      studio: '',
      runtime: '',
      description: '',
    });
    setNewCollectionItem({
      personal_notes: '',
      format: 'VHS',
    });

    // Clear any pending poster downloads
    window.pendingPosterDownload = null;
  };

  // Seller rating functions
  const getSellerRating = useCallback(
    async sellerId => {
      try {
        const { data, error } = await supabase
          .from('seller_ratings')
          .select('*')
          .eq('seller_id', sellerId);

        if (error) {
          console.error('❌ Error fetching seller ratings:', error);
          return { average: 0, count: 0, ratings: [] };
        }

        const ratings = data || [];
        const average =
          ratings.length > 0 ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length : 0;

        return {
          average: Math.round(average * 10) / 10,
          count: ratings.length,
          ratings: ratings,
        };
      } catch (error) {
        console.error('❌ Error in getSellerRating:', error);
        return { average: 0, count: 0, ratings: [] };
      }
    },
    [supabase]
  );

  const addSellerRating = useCallback(
    async (sellerId, buyerId, rating, reviewText = null, listingId = null) => {
      try {
        const { data, error } = await supabase
          .from('seller_ratings')
          .insert({
            seller_id: sellerId,
            buyer_id: buyerId,
            listing_id: listingId,
            rating: rating,
            review_text: reviewText,
          })
          .select()
          .single();

        if (error) {
          console.error('❌ Error adding seller rating:', error);
          throw error;
        }

        return { success: true, data };
      } catch (error) {
        console.error('❌ Error in addSellerRating:', error);
        return { success: false, error: error.message };
      }
    },
    [supabase]
  );

  // Activity feed function - Load recent community activity
  const loadActivityFeed = useCallback(async () => {
    console.log('📈 Loading community activity feed...');

    try {
      const activities = [];
      const now = new Date();

      // Helper function to format relative time
      const getRelativeTime = dateStr => {
        const date = new Date(dateStr);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) {
          return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
        } else if (diffHours < 24) {
          return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
        } else {
          return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
        }
      };

      // 1. Recent collection additions (last 20)
      const { data: collectionActivities, error: collectionError } = await supabase
        .from('user_collection_items')
        .select(
          `
          created_at,
          user_profiles!inner(username),
          movie_releases!inner(
            id,
            release_title,
            studio,
            distributor_code,
            media_titles!master_movie_id(
              id, title, year
            )
          )
        `
        )
        .not('release_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!collectionError && collectionActivities) {
        collectionActivities.forEach(item => {
          const movie = item.movie_releases.media_titles;

          let displayTitle = movie.title;
          if (item.movie_releases.studio) {
            displayTitle += ` • ${item.movie_releases.studio}`;
            if (item.movie_releases.distributor_code) {
              displayTitle += ` ${item.movie_releases.distributor_code}`;
            }
          }

          activities.push({
            id: `collection-${item.created_at}-${item.user_profiles.username}`,
            user: item.user_profiles.username,
            action: 'added to collection',
            item: displayTitle,
            time: getRelativeTime(item.created_at),
            type: 'collection',
            timestamp: item.created_at,
            movie_id: movie.id,
            movie_title: movie.title,
            release_id: item.movie_releases.id,
            release_title: item.movie_releases.release_title,
          });
        });
      }

      // 2. Recent marketplace listings (last 20)
      const { data: marketplaceActivities, error: marketplaceError } = await supabase
        .from('marketplace_listings')
        .select(
          `
          created_at,
          price,
          user_profiles!inner(username),
          movie_releases!inner(
            id,
            release_title,
            media_titles!master_movie_id(
              id, title, year
            )
          )
        `
        )
        .eq('is_active', true)
        .not('release_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!marketplaceError && marketplaceActivities) {
        marketplaceActivities.forEach(item => {
          const movie = item.movie_releases.media_titles;
          const releaseTitle = item.movie_releases.release_title || 'Standard Release';
          activities.push({
            id: `marketplace-${item.created_at}-${item.user_profiles.username}`,
            user: item.user_profiles.username,
            action: 'listed for sale',
            item: `${movie.title} (${movie.year}) - ${releaseTitle} ($${item.price})`,
            time: getRelativeTime(item.created_at),
            type: 'marketplace',
            timestamp: item.created_at,
            movie_id: movie.id,
            movie_title: movie.title,
            release_id: item.movie_releases.id,
            release_title: item.movie_releases.release_title,
          });
        });
      }

      // 3. Recent wantlist additions (last 20)
      const { data: wantlistActivities, error: wantlistError } = await supabase
        .from('user_wantlists')
        .select(
          `
          created_at,
          user_profiles!inner(username),
          movie_releases!inner(
            id,
            release_title,
            media_titles!master_movie_id(
              id, title, year
            )
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(20);

      if (!wantlistError && wantlistActivities) {
        wantlistActivities.forEach(item => {
          const movie = item.movie_releases.media_titles;
          const releaseTitle = item.movie_releases.release_title || 'Standard Release';
          activities.push({
            id: `wantlist-${item.created_at}-${item.user_profiles.username}`,
            user: item.user_profiles.username,
            action: 'wants',
            item: `${movie.title} (${movie.year}) - ${releaseTitle}`,
            time: getRelativeTime(item.created_at),
            type: 'wantlist',
            timestamp: item.created_at,
            movie_id: movie.id,
            movie_title: movie.title,
            release_id: item.movie_releases.id,
            release_title: item.movie_releases.release_title,
          });
        });
      }

      // 4. Recent forum posts (last 20)
      const { data: forumActivities, error: forumError } = await supabase
        .from('forum_posts')
        .select(
          `
          created_at,
          title,
          category,
          user_profiles!inner(username)
        `
        )
        .order('created_at', { ascending: false })
        .limit(20);

      if (!forumError && forumActivities) {
        forumActivities.forEach(item => {
          activities.push({
            id: `forum-${item.created_at}-${item.user_profiles.username}`,
            user: item.user_profiles.username,
            action: 'posted in forum',
            item: `"${item.title}" in ${item.category}`,
            time: getRelativeTime(item.created_at),
            type: 'forum',
            timestamp: item.created_at,
          });
        });
      }

      // 5. New movies added to catalog (last 10)
      const { data: movieActivities, error: movieError } = await supabase
        .from('media_titles')
        .select(
          `
          id,
          created_at,
          title,
          year,
          user_profiles!inner(username)
        `
        )
        .not('added_by', 'is', null)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!movieError && movieActivities) {
        movieActivities.forEach(item => {
          activities.push({
            id: `movie-${item.created_at}-${item.user_profiles.username}`,
            user: item.user_profiles.username,
            action: 'added movie to catalog',
            item: `${item.title} (${item.year})`,
            time: getRelativeTime(item.created_at),
            type: 'catalog',
            timestamp: item.created_at,
            movie_id: item.id,
            movie_title: item.title,
          });
        });
      }

      // 4. Recent poster uploads (last 20)
      const { data: posterActivities, error: posterError } = await supabase
        .from('master_item_images')
        .select(
          `
          created_at,
          uploaded_by,
          master_item_id,
          media_titles!master_movie_id(
            title, year
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(20);

      if (!posterError && posterActivities) {
        // Get user profiles for the uploaded_by user IDs
        const userIds = [...new Set(posterActivities.map(item => item.uploaded_by))];
        const { data: userProfiles, error: userError } = await supabase
          .from('user_profiles')
          .select('user_id, username')
          .in('user_id', userIds);

        if (!userError && userProfiles) {
          const userMap = userProfiles.reduce((map, user) => {
            map[user.user_id] = user.username;
            return map;
          }, {});

          posterActivities.forEach(item => {
            const movie = item.media_titles;
            const username = userMap[item.uploaded_by];
            if (username && movie) {
              activities.push({
                id: `poster-${item.created_at}-${username}`,
                user: username,
                action: 'updated poster',
                item: `${movie.title} (${movie.year})`,
                time: getRelativeTime(item.created_at),
                type: 'poster',
                timestamp: item.created_at,
              });
            }
          });
        }
      }

      // Sort all activities by timestamp (most recent first) and limit to 30
      const sortedActivities = activities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 30)
        .map(activity => {
          // Remove timestamp from final output
          const { timestamp, ...activityWithoutTimestamp } = activity;
          return activityWithoutTimestamp;
        });

      console.log('✅ Loaded activity feed:', sortedActivities.length, 'activities');
      return sortedActivities;
    } catch (error) {
      console.error('❌ Error loading activity feed:', error);
      return [];
    }
  }, [supabase]);

  // Load user's wantlist (now with releases)
  const loadUserWantlist = useCallback(async () => {
    if (!currentUser || !supabase) {
      console.log('❌ Wantlist loading skipped - no user or supabase');
      return;
    }

    // Loading user wantlist

    try {
      const { data, error } = await supabase
        .from('user_wantlists')
        .select(
          `
          id,
          release_id,
          master_movie_id,
          notes,
          created_at,
          movie_releases (
            id,
            display_id,
            master_movie_id,
            media_titles!master_movie_id (
              id, display_id, title, year, genre, director, studio, runtime, description, cover_image_url
            )
          ),
          media_titles!master_movie_id (
            id, display_id, title, year, genre, director, studio, runtime, description, cover_image_url
          )
        `
        )
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error loading wantlist:', error);
        return;
      }

      setUserWantlist(data || []);
    } catch (error) {
      console.error('❌ Error in loadUserWantlist:', error);
    }
  }, [supabase, currentUser]);

  // Add release to wantlist (by release display_id)
  const addReleaseToWantlist = useCallback(
    async (releaseDisplayId, notes = '') => {
      if (!currentUser || !supabase) return { success: false, error: 'Not authenticated' };

      try {
        // First, get the UUID for this release display_id
        const { data: release, error: findError } = await supabase
          .from('movie_releases')
          .select('id, display_id')
          .eq('display_id', releaseDisplayId)
          .single();

        if (findError || !release) {
          console.error('❌ Error finding release:', findError);
          return { success: false, error: 'Release not found' };
        }

        const { data, error } = await supabase
          .from('user_wantlists')
          .insert([
            {
              user_id: currentUser.id,
              release_id: release.id,
              notes: notes,
            },
          ])
          .select()
          .single();

        if (error) {
          if (error.code === '23505') {
            showError('Item already in wantlist');
            return { success: false, error: 'Item already in wantlist' };
          }
          console.error('❌ Error adding to wantlist:', error);
          showError('Error adding to wantlist: ' + error.message);
          return { success: false, error: error.message };
        }

        // Reload wantlist to get updated data
        await loadUserWantlist();
        showSuccess('Added to your wantlist!');
        return { success: true, data };
      } catch (error) {
        console.error('❌ Error in addToWantlist:', error);
        showError('Error adding to wantlist: ' + error.message);
        return { success: false, error: error.message };
      }
    },
    [supabase, currentUser, loadUserWantlist, showError, showSuccess]
  );

  // Remove release from wantlist (by release display_id)
  const removeReleaseFromWantlist = useCallback(
    async releaseDisplayId => {
      if (!currentUser || !supabase) return { success: false, error: 'Not authenticated' };

      try {
        // First, get the UUID for this release display_id
        const { data: release, error: findError } = await supabase
          .from('movie_releases')
          .select('id')
          .eq('display_id', releaseDisplayId)
          .single();

        if (findError || !release) {
          console.error('❌ Error finding release:', findError);
          return { success: false, error: 'Release not found' };
        }

        const { error } = await supabase
          .from('user_wantlists')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('release_id', release.id);

        if (error) {
          console.error('❌ Error removing from wantlist:', error);
          showError('Error removing from wantlist: ' + error.message);
          return { success: false, error: error.message };
        }

        // Update local state
        setUserWantlist(prev =>
          prev.filter(item => item.movie_releases?.display_id !== releaseDisplayId)
        );
        showSuccess('Removed from your wantlist!');
        return { success: true };
      } catch (error) {
        console.error('❌ Error in removeReleaseFromWantlist:', error);
        showError('Error removing from wantlist: ' + error.message);
        return { success: false, error: error.message };
      }
    },
    [supabase, currentUser, showError, showSuccess]
  );

  // Check if release is in wantlist (by release display_id)
  const isReleaseWanted = useCallback(
    releaseDisplayId => {
      return userWantlist.some(item => item.movie_releases?.display_id === releaseDisplayId);
    },
    [userWantlist]
  );

  // Check if any release of a movie is wanted (for catalog hearts)
  const hasWantedReleases = useCallback(
    movieId => {
      return userWantlist.some(item => item.movie_releases?.master_movie_id === movieId);
    },
    [userWantlist]
  );

  // Add movie to wantlist (by media_titles.id or display_id)
  const addMovieToWantlist = useCallback(
    async (movieId, notes = '') => {
      if (!currentUser || !supabase) return { success: false, error: 'Not authenticated' };

      try {
        // movieId could be either a media_titles.id (UUID) or display_id (integer)
        let mediaTitle;

        if (typeof movieId === 'string' && movieId.length === 36) {
          // Looks like a UUID, query by id
          const { data, error } = await supabase
            .from('media_titles')
            .select('id, display_id, title')
            .eq('id', movieId)
            .single();

          if (error || !data) {
            console.error('❌ Error finding movie by ID:', error);
            return { success: false, error: 'Movie not found' };
          }
          mediaTitle = data;
        } else {
          // Assume it's a display_id
          const { data, error } = await supabase
            .from('media_titles')
            .select('id, display_id, title')
            .eq('display_id', movieId)
            .single();

          if (error || !data) {
            console.error('❌ Error finding movie by display_id:', error);
            return { success: false, error: 'Movie not found' };
          }
          mediaTitle = data;
        }

        // Add to wantlist using master_movie_id (references media_titles.id)
        // Adding movie to wantlist

        const { data, error } = await supabase
          .from('user_wantlists')
          .insert([
            {
              user_id: currentUser.id,
              master_movie_id: mediaTitle.id,
              notes: notes,
            },
          ])
          .select()
          .single();

        // Wantlist insert completed

        if (error) {
          if (error.code === '23505') {
            showError('Movie already in wantlist');
            return { success: false, error: 'Movie already in wantlist' };
          }
          console.error('❌ Error adding movie to wantlist:', error);
          showError('Error adding to wantlist: ' + error.message);
          return { success: false, error: error.message };
        }

        // Reload wantlist to get updated data
        await loadUserWantlist();
        showSuccess(`Added "${mediaTitle.title}" to your wantlist!`);
        return { success: true, data };
      } catch (error) {
        console.error('❌ Error in addMovieToWantlist:', error);
        showError('Error adding to wantlist: ' + error.message);
        return { success: false, error: error.message };
      }
    },
    [supabase, currentUser, loadUserWantlist, showSuccess, showError]
  );

  // Remove movie from wantlist (by media_titles.id or display_id)
  const removeMovieFromWantlist = useCallback(
    async movieId => {
      if (!currentUser || !supabase) return { success: false, error: 'Not authenticated' };

      try {
        // movieId could be either a media_titles.id (UUID) or display_id (integer)
        let mediaTitle;

        if (typeof movieId === 'string' && movieId.length === 36) {
          // Looks like a UUID, query by id
          const { data, error } = await supabase
            .from('media_titles')
            .select('id, display_id, title')
            .eq('id', movieId)
            .single();

          if (error || !data) {
            console.error('❌ Error finding movie by ID:', error);
            return { success: false, error: 'Movie not found' };
          }
          mediaTitle = data;
        } else {
          // Assume it's a display_id
          const { data, error } = await supabase
            .from('media_titles')
            .select('id, display_id, title')
            .eq('display_id', movieId)
            .single();

          if (error || !data) {
            console.error('❌ Error finding movie by display_id:', error);
            return { success: false, error: 'Movie not found' };
          }
          mediaTitle = data;
        }

        const { error } = await supabase
          .from('user_wantlists')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('master_movie_id', mediaTitle.id);

        if (error) {
          console.error('❌ Error removing movie from wantlist:', error);
          showError('Error removing from wantlist: ' + error.message);
          return { success: false, error: error.message };
        }

        // Update local state
        setUserWantlist(prev => prev.filter(item => item.master_movie_id !== mediaTitle.id));
        showSuccess(`Removed "${mediaTitle.title}" from your wantlist!`);
        return { success: true };
      } catch (error) {
        console.error('❌ Error in removeMovieFromWantlist:', error);
        showError('Error removing from wantlist: ' + error.message);
        return { success: false, error: error.message };
      }
    },
    [supabase, currentUser, showSuccess, showError]
  );

  // Check if movie is in wantlist (by media_titles.id or display_id)
  const isMovieWanted = useCallback(
    movieId => {
      if (!movieId) return false;

      // Check by display_id first (more common case)
      if (typeof movieId === 'number' || (typeof movieId === 'string' && !movieId.includes('-'))) {
        // Look for display_id match in userWantlist
        return userWantlist.some(
          item =>
            item.media_titles?.display_id === parseInt(movieId) || item.master_movie_id === movieId
        );
      }

      // Check by UUID
      return userWantlist.some(item => item.master_movie_id === movieId);
    },
    [userWantlist]
  );

  // Load wantlist when user changes
  useEffect(() => {
    if (authLoading) return;
    if (currentUser) {
      loadUserWantlist();
    } else {
      setUserWantlist([]);
    }
  }, [currentUser, loadUserWantlist, authLoading]);

  return {
    // State
    masterItems,
    allReleases,
    userCollectionItems,
    marketplaceListings,
    userWantlist,
    newMasterItem,
    setNewMasterItem,
    newCollectionItem,
    setNewCollectionItem,

    // Load functions
    loadMasterItems,
    loadAllReleases,
    loadUserCollection,
    loadMarketplaceListings,
    loadMovieReleases, // ← MAKE SURE THIS LINE EXISTS
    loadMasterItemImages,
    loadReleaseImages,

    // Image functions
    uploadMasterItemImages,
    saveMasterItemImages,
    cleanupMasterItemImages,

    // Validation
    validateMasterItem,
    checkForDuplicates,

    // CRUD operations
    handleAddMasterItem,
    handleEditMasterItem,
    handleDeleteMasterItem,
    addToCollection,
    addToCollectionWithRelease,
    removeFromCollection,
    updateCollectionNotes,
    addMarketplaceListing,
    removeMarketplaceListing,
    removeFromCollectionAndMarketplace,

    // Utility functions
    isItemListed,
    resetForms,

    // Seller rating functions
    getSellerRating,
    addSellerRating,

    // Activity feed functions
    loadActivityFeed,

    // Wantlist functions
    loadUserWantlist,
    addReleaseToWantlist,
    removeReleaseFromWantlist,
    isReleaseWanted,
    hasWantedReleases,
    addMovieToWantlist,
    removeMovieFromWantlist,
    isMovieWanted,
  };
};
